/**
 * About — `/about`. Public "how it works" explainer. The elevator pitch
 * for a first-time visitor arriving from an SEO landing page or a friend's
 * WhatsApp: three plain-English steps that mirror our actual product surface
 * (Quick Check → Advanced Match → Verify at source), then the trust badge
 * links (Methodology, Corrections log) that back the claim.
 *
 * Deliberately short. Anything requiring a footnote lives on /methodology.
 */
import { Link } from "react-router-dom";
import { Compass, Sparkles, ShieldCheck, ArrowRight, ScrollText } from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";

export default function About() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10" data-testid="about-page">
      <PathwaySEO
        title="How WhichUniversityAdvisor works — Quick Check, Advanced Match, Verify at source"
        description="A comparison site for degrees, not a ranking site. Answer three questions to get a Quick Check, refine with Advanced Match against 12,000+ degrees, then verify every number at its named public source."
        path="/about"
      />

      {/* Hero */}
      <div>
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <ScrollText className="h-3 w-3" /> About WUA
        </div>
        <h1
          data-testid="about-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          How WhichUniversityAdvisor works
        </h1>
        <p className="mt-4 text-stone-700 max-w-3xl leading-relaxed text-lg">
          We're a comparison site for degrees — not a ranking site.
          Every number on WUA is sourced from a named public dataset with a
          dated citation, and every claim is one click from its footnote.
          Here's the whole product in three steps.
        </p>
      </div>

      {/* Three steps */}
      <div className="mt-12 space-y-6" data-testid="about-steps">
        <StepCard
          number="01"
          icon={<Compass className="h-5 w-5" />}
          title="Quick Check"
          kicker="30 seconds · no account"
          body={
            <>
              <p>
                Answer three questions — what you want to study, roughly
                where, and what grades you're realistically holding — and
                we return a shortlist of degrees you're likely to get an
                offer for, ranked by career outcome, not by prestige.
              </p>
              <p className="mt-3">
                Every card shows the median starting salary, the top three
                destination employers for that cohort, and the honest odds
                of admission at your grade profile. No lock-in, no email
                wall — just the answer.
              </p>
            </>
          }
          cta={{ to: "/admission", label: "Try the Quick Check", testid: "about-cta-quick-check" }}
        />

        <StepCard
          number="02"
          icon={<Sparkles className="h-5 w-5" />}
          title="Advanced Match"
          kicker="For when the shortlist matters"
          body={
            <>
              <p>
                When you're ready to commit, Advanced Match runs your full
                situation — grades, budget envelope (tuition + living), visa
                needs, target firms, teaching-style preference, city
                personality — against every one of the 12,000+ degrees in
                our catalog and returns a ranked, honest shortlist.
              </p>
              <p className="mt-3">
                Each match tells you <em>why</em> it fits (Financial fit,
                Personal fit, Course DNA fit) and <em>where it falls
                short</em>, so a rejection is just as useful as an offer.
              </p>
            </>
          }
          cta={{ to: "/advanced", label: "Run an Advanced Match", testid: "about-cta-advanced" }}
        />

        <StepCard
          number="03"
          icon={<ShieldCheck className="h-5 w-5" />}
          title="Verify at source"
          kicker="Every number has a footnote"
          body={
            <>
              <p>
                Open any degree page and you'll see a "Verify at source"
                panel: the exact HESA / UCAS / DAAD / US College Scorecard
                / CAO row we pulled the number from, dated, one tap away.
                If we've curated the destination-firms list, we say so and
                show the Signal Band (Strong / Regular / Emerging).
              </p>
              <p className="mt-3">
                Get something wrong? Every correction we've made this year
                is public. Report a fix in one tap and it lands in the same
                queue our editors work off.
              </p>
            </>
          }
          cta={{
            to: "/methodology",
            label: "Read the full methodology",
            testid: "about-cta-methodology",
          }}
        />
      </div>

      {/* Trust strip */}
      <div
        className="mt-14 border-t border-stone-200 pt-8"
        data-testid="about-trust-strip"
      >
        <div className="label-uppercase text-[#4C1D95]">Trust</div>
        <h2 className="mt-1 font-editorial text-2xl text-[#002147]">
          Why we're different from a ranking site
        </h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2 text-[14px] text-stone-700">
          <TrustPoint text="No paid placements. Firms can claim a profile and add events, but they can't buy a higher rank." />
          <TrustPoint text="Four data tiers (Editorial / Verified / Directory / Non-HE). The tier is always visible on the page." />
          <TrustPoint text="Career outcomes come first — median salary + destination employers sit above the fold, not tucked in a PDF." />
          <TrustPoint text="Every correction we've published this year is on the public log. Nothing gets quietly rewritten." />
        </ul>
        <div className="mt-6 flex flex-wrap gap-3 text-[13px]">
          <Link
            to="/methodology"
            data-testid="about-link-methodology"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95] bg-[#4C1D95]/[0.03] px-3 py-1.5 font-underground-sc tracking-wider text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white transition-colors"
          >
            Full methodology <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to="/methodology/corrections"
            data-testid="about-link-corrections"
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 font-underground-sc tracking-wider text-stone-700 hover:bg-stone-100 transition-colors"
          >
            Corrections log <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to="/legal/contact"
            data-testid="about-link-contact"
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 font-underground-sc tracking-wider text-stone-700 hover:bg-stone-100 transition-colors"
          >
            Contact us <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function StepCard({ number, icon, title, kicker, body, cta }) {
  return (
    <div
      className="grid gap-6 sm:grid-cols-[auto_1fr] rounded-2xl border border-stone-200 bg-white p-6 sm:p-8"
      data-testid={`about-step-${number}`}
    >
      <div className="flex sm:flex-col items-start sm:items-center gap-3 sm:gap-2 sm:w-24">
        <div className="font-chip text-4xl text-[#4C1D95] leading-none tabular-nums">
          {number}
        </div>
        <div className="rounded-full bg-[#4C1D95]/[0.08] p-2 text-[#4C1D95]">
          {icon}
        </div>
      </div>
      <div>
        <div className="label-uppercase text-stone-500">{kicker}</div>
        <h3 className="mt-1 font-editorial text-2xl text-[#002147]">
          {title}
        </h3>
        <div className="mt-3 text-[14px] text-stone-700 leading-relaxed space-y-2">
          {body}
        </div>
        {cta && (
          <Link
            to={cta.to}
            data-testid={cta.testid}
            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
          >
            {cta.label} <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </div>
  );
}

function TrustPoint({ text }) {
  return (
    <li className="flex gap-2">
      <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-[#4C1D95] flex-shrink-0" />
      <span>{text}</span>
    </li>
  );
}
